import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogHeading,
  DialogBody,
  Heading,
} from '@global-ecom/nitro-uds/elements';

import {
  useModalsOpenStore,
  usePopupsOpenStore,
  useTempStateStore,
} from 'store';
import {
  Product,
  ProductCartItem,
  ProductWithExclusion,
  Size,
  Variant,
} from '__generated__/graphql';
import { ActiveCart } from 'types/cart';
import { Link } from 'UDS/elements/action-cta'; // our modified clone of UDS.Link component - TODO: later consume this from our "UDS" repo
import { useMiniCart } from 'hooks/useMiniCart';
import { getIsMobile } from 'utils/media';
import { useTranslate } from 'hooks/useTranslations';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { AnalyticsEvents, event } from 'utils/analytics';

import { GWPNew_Product } from './GWP.new.Product';
import { GWPNew_ProductSelector } from './GWP.new.ProductSelector';
import { GWPNew_AddToCartButton } from './GWP.new.AddToCartButton';

export const GWPNew_Modal = ({ cart }: { cart: ActiveCart }) => {
  const t = useTranslate();
  const router = useRouter();
  const { setModalOpen } = useModalsOpenStore();
  const { openMiniCart } = useMiniCart();
  const { setPopupOpen } = usePopupsOpenStore();
  const { pageviewEventHasFired } = usePageEventsContext();
  const { getTempState } = useTempStateStore();

  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>();
  const [selectedSize, setSelectedSize] = useState<Size | undefined>();

  const lastGWPAdded =
    cart?.giftWithPurchases[cart?.giftWithPurchases.length - 1];

  const products = lastGWPAdded?.availableProducts
    .filter(a => a.__typename === 'ProductWithExclusion')
    .map(a => (a as ProductWithExclusion).product);

  // set the first available product as selected product
  useEffect(() => {
    if (!selectedProduct) {
      if (
        lastGWPAdded?.availableProducts[0].__typename === 'ProductWithExclusion'
      ) {
        setSelectedProduct(
          lastGWPAdded?.availableProducts[0].product as Product
        );
      }
    }
  }, [selectedProduct, lastGWPAdded]);

  const openPopupOrMiniCartAfterClosingModal = useCallback(() => {
    // don't try to open popup or miniCart when user is on Cart page
    if (!router.asPath.includes('/cart')) {
      if (getIsMobile()) {
        // in mobile show cart-button-popup
        setPopupOpen('CartButtonPopup', true);
      } else {
        // in desktop show mini-cart with the last item added to the cart

        // NOTE:  we need to use the `getTempState('IdOfLastProductAddedToCart')` function
        //        to filter the product in the cart by this id, because when we add the
        //        same product in the cart for the 2nd time and with another product in
        //        between, we only are updating the quantity field and no "adding" new item
        //        to the array of product objects. So we cannot get the last item of the
        //        cart by trying to get the last item of the array.
        const lastProductAddedToTheCart = (
          cart?.products as ProductCartItem[] | undefined
        )?.find(
          p => p.product.id === getTempState('IdOfLastProductAddedToCart')
        );

        if (lastProductAddedToTheCart)
          openMiniCart(lastProductAddedToTheCart, true);
      }
    }
  }, [cart?.products, getTempState, openMiniCart, router.asPath, setPopupOpen]);

  useEffect(() => {
    if (pageviewEventHasFired) {
      event(AnalyticsEvents.GA4_CustomEvent, {
        event_name: AnalyticsEvents.GA4_OVERLAY_VIEW,
      });
    }
  }, [pageviewEventHasFired]);
  if (
    !selectedProduct?.variations ||
    !selectedProduct.name ||
    !lastGWPAdded?.id
  )
    return null;

  return (
    <DialogRoot
      dataTestId="gwp-new-modal"
      size={{ _: 'full', xs: 'sm' }}
      drawer={{ _: false, xs: true }}
      justify="end"
      open
      onOpenChange={open => {
        // the modal doesn't open from here, so only handle close-logic
        if (!open) {
          setModalOpen('GWPModal', false);
          openPopupOrMiniCartAfterClosingModal();
        }
      }}
      autoFocus="content"
    >
      <DialogContent onInteractOutside={openPopupOrMiniCartAfterClosingModal}>
        <DialogHeader className="min-h-[75px] pr-7 flex flex-col">
          <DialogHeading>{lastGWPAdded?.gwpTitle}</DialogHeading>
          <Heading asChild size="xxs" weight="normal" className="mt-2">
            {t('giftWithPurchaseModalSelectYourItems')}
          </Heading>
        </DialogHeader>
        <DialogBody className="overflow-y-auto overflow-x-hidden">
          {products && products.length > 1 ? (
            <GWPNew_ProductSelector
              currentItem={selectedProduct}
              setCurrentItem={setSelectedProduct}
              items={products}
            />
          ) : null}
          <GWPNew_Product
            {...{
              selectedProduct,
              selectedVariant,
              setSelectedVariant,
              selectedSize,
              setSelectedSize,
            }}
          />
        </DialogBody>
        <div className="mb-2 flex justify-center flex-col p-4">
          <GWPNew_AddToCartButton
            gwpId={lastGWPAdded?.id}
            {...{ selectedSize, selectedVariant }}
          />
          <Link
            variant="underline"
            href="/puma/sale-terms-and-conditions"
            target="_blank" // open new tab
            label={t('termsAmpersandConditions')}
            className="text-gray-500 hover:text-black"
            uppercase={false}
            bold={false}
            underlineSizeInPx="1px"
            size="lg"
          />
        </div>
      </DialogContent>
    </DialogRoot>
  );
};
